import { http } from '../index'

// ==================== 配置字段 ====================

export interface ConfigField {
  id: number
  group: string // 配置分组：site, system, security, etc.
  type: string // 字段类型
  environment?: string // 配置所属环境
  key: string // 字段键名
  name: string // 字段标签
  description?: string // 字段描述
  options?: string | null // 字段选项（JSON字符串）
  default_value?: string | null // 默认值
  value?: string | null // 当前值
  is_required: boolean // 是否必填
  is_encrypted: boolean // 是否加密
  is_visible: boolean // 是否可见
  is_editable: boolean // 是否可编辑
  sort: number // 排序
  validation_rules?: string | null // 验证规则（JSON字符串）
  help_text?: string | null // 帮助文本
  created_at?: string
  updated_at?: string
}

export interface ConfigFieldListParams {
  page?: number
  per_page?: number
  keyword?: string // 关键词
  group?: string // 配置分组筛选
  type?: string // 配置类型筛选
  environment?: string // 环境筛选
}

export interface ConfigValue {
  [key: string]: any // 配置值的键值对
}

export interface ConfigValueUpdateData {
  configs: Array<{
    field_name: string
    value: any
  }>
  environment?: string
}

export interface ConfigBatchGetParams {
  keys?: string[]
  group?: string
  environment?: string
}

const KEY_CANDIDATES = ['field_name', 'key', 'name', 'config_key', 'fieldName']
const VALUE_CANDIDATES = ['value', 'field_value', 'config_value', 'default_value', 'content', 'val']

function normalizeConfigResponse(data: any): ConfigValue {
  const normalized: ConfigValue = {}

  const pickKeyValue = (item: any): { key: string, value: any } | null => {
    if (!item || typeof item !== 'object') {
      return null
    }

    const keyCandidate = KEY_CANDIDATES.find(candidate => Object.prototype.hasOwnProperty.call(item, candidate))
    if (!keyCandidate) {
      return null
    }

    const key = item[keyCandidate]
    if (typeof key !== 'string' || !key.length) {
      return null
    }

    const valueCandidate = VALUE_CANDIDATES.find(candidate => Object.prototype.hasOwnProperty.call(item, candidate))
    const value = valueCandidate ? item[valueCandidate] : undefined

    return { key, value }
  }

  const assignLeafObject = (record: Record<string, any>) => {
    Object.entries(record).forEach(([key, value]) => {
      normalized[key] = value
    })
  }

  const walker = (node: any) => {
    if (node == null) {
      return
    }

    if (Array.isArray(node)) {
      node.forEach(item => walker(item))
      return
    }

    if (typeof node !== 'object') {
      return
    }

    const kv = pickKeyValue(node)
    if (kv) {
      normalized[kv.key] = kv.value
      return
    }

    const entries = Object.entries(node)
    if (!entries.length) {
      return
    }

    const isPlainLeaf = entries.every(([, value]) => value == null || typeof value !== 'object')

    if (isPlainLeaf) {
      assignLeafObject(node as Record<string, any>)
      return
    }

    entries.forEach(([, value]) => walker(value))
  }

  walker(data)
  return normalized
}

async function fetchConfigKeysByGroup(group: string): Promise<string[]> {
  try {
    const res = await http.get<{
      list: ConfigField[]
      total: number
      current_page: number
      per_page: number
      last_page: number
    }>('system/configs', {
      params: {
        group,
        page: 1,
        per_page: 1000,
      },
    })

    return (res?.data?.list ?? [])
      .map(item => item.key || item.name || '')
      .filter((key): key is string => typeof key === 'string' && key.length > 0)
  }
  catch (error) {
    console.error('Failed to fetch config keys for group:', group, error)
    return []
  }
}

async function requestBatchConfigValues(params: ConfigBatchGetParams = {}) {
  const { environment } = params
  let resolvedKeys = params.keys?.filter(Boolean) ?? []

  if (!resolvedKeys.length) {
    if (params.group) {
      resolvedKeys = await fetchConfigKeysByGroup(params.group)
    }
    else {
      // 没有提供 keys 和 group，直接返回全部配置
      const res = await http.get<any>('system/configs/all')
      const normalized = normalizeConfigResponse(res?.data)
      return {
        ...res,
        data: normalized,
      }
    }
  }

  if (!resolvedKeys.length) {
    return Promise.resolve({
      code: 0,
      message: 'success',
      data: {},
    })
  }

  const payload: Record<string, any> = { keys: resolvedKeys }
  if (environment) {
    payload.environment = environment
  }

  return http.post('system/configs/batch-get', payload).then((res: any) => {
    const normalized = normalizeConfigResponse(res?.data)
    return {
      ...res,
      data: normalized,
    }
  })
}

// ==================== 网站基本信息 ====================

export interface SiteInfo {
  id?: number
  site_name: string
  site_logo: string
  site_icon: string
  site_title: string
  site_keywords: string
  site_description: string
  site_icp: string
  site_copyright: string
  contact_phone: string
  contact_email: string
  contact_address: string
  contact_qq: string
  contact_wechat: string
  contact_qq_qrcode: string
  contact_wechat_qrcode: string
  about_us: string
  created_at?: string
  updated_at?: string
}

export interface SiteInfoUpdateData extends Partial<SiteInfo> {}

// ==================== 系统配置 ====================

export interface SystemConfig {
  id?: number
  // 上传配置
  upload_max_size: number // MB
  upload_allowed_types: string[]
  upload_provider: string
  upload_access_key: string
  upload_secret_key: string
  upload_bucket: string
  upload_region: string
  upload_domain: string

  // 邮件配置
  mail_host: string
  mail_port: number
  mail_username: string
  mail_password: string
  mail_from_address: string
  mail_from_name: string

  // 短信配置
  sms_provider: string
  sms_access_key: string
  sms_secret_key: string
  sms_sign_name: string

  // 其他配置
  enable_register: boolean
  enable_comment: boolean
  enable_captcha: boolean
  maintenance_mode: boolean
  maintenance_message: string

  created_at?: string
  updated_at?: string
}

export interface SystemConfigUpdateData extends Partial<SystemConfig> {}

// ==================== 站点设置 ====================

export interface SiteSettings {
  id?: number
  // 功能开关
  enable_user_register: boolean
  enable_user_login: boolean
  enable_comment: boolean
  enable_like: boolean
  enable_share: boolean

  // 安全设置
  password_min_length: number
  password_require_number: boolean
  password_require_letter: boolean
  password_require_special: boolean
  login_max_attempts: number
  login_lock_duration: number // 分钟

  // 验证码设置
  enable_login_captcha: boolean
  enable_register_captcha: boolean
  enable_comment_captcha: boolean
  captcha_type: string // 'image' | 'sms' | 'email'

  // 会话设置
  session_timeout: number // 分钟
  remember_me_duration: number // 天

  // 其他设置
  default_avatar: string
  default_cover: string
  page_size: number
  max_upload_size: number // MB

  created_at?: string
  updated_at?: string
}

export interface SiteSettingsUpdateData extends Partial<SiteSettings> {}

// ==================== 系统字典 ====================

export interface Dictionary {
  id?: number
  dict_type: string // 字典类型
  dict_code: string // 字典编码
  dict_label: string // 字典标签
  dict_value: string // 字典值
  dict_sort: number // 排序
  status: number // 状态：0-禁用 1-启用
  remark?: string // 备注
  created_at?: string
  updated_at?: string
}

export interface DictionaryListParams {
  page?: number
  per_page?: number
  dict_type?: string
  dict_code?: string
  dict_label?: string
  status?: number
}

export interface DictionaryCreateData {
  dict_type: string
  dict_code: string
  dict_label: string
  dict_value: string
  dict_sort?: number
  status?: number
  remark?: string
}

export interface DictionaryUpdateData extends Partial<DictionaryCreateData> {}

// 字典类型
export interface DictionaryType {
  id?: number
  type_code: string // 类型编码
  type_name: string // 类型名称
  status: number // 状态
  remark?: string
  created_at?: string
  updated_at?: string
}

export interface DictionaryTypeListParams {
  page?: number
  per_page?: number
  type_code?: string
  type_name?: string
  status?: number
}

export interface DictionaryTypeCreateData {
  type_code: string
  type_name: string
  status?: number
  remark?: string
}

export interface DictionaryTypeUpdateData extends Partial<DictionaryTypeCreateData> {}

// ==================== API 接口 ====================

export const settingsApi = {
  // ==================== 配置字段 ====================

  /**
   * 获取配置字段列表
   */
  getConfigFields: (params?: ConfigFieldListParams) => {
    return http.get<{
      list: ConfigField[]
      current_page: number
      per_page: number
      total: number
      last_page: number
    }>('system/configs', { params })
  },

  /**
   * 获取配置字段详情
   */
  getConfigFieldDetail: (id: number) => {
    return http.get<ConfigField>(`system/configs/${id}`)
  },

  /**
   * 创建配置字段
   */
  createConfigField: (data: Partial<ConfigField>) => {
    return http.post('system/configs', data)
  },

  /**
   * 更新配置字段
   */
  updateConfigField: (id: number, data: Partial<ConfigField>) => {
    return http.put(`system/configs/${id}`, data)
  },

  /**
   * 删除配置字段
   */
  deleteConfigField: (id: number) => {
    return http.delete(`system/configs/${id}`)
  },

  /**
   * 批量删除配置字段
   */
  batchDeleteConfigFields: (ids: number[]) => {
    return http.post('system/configs/batch-delete', { ids })
  },

  /**
   * 获取配置值
   */
  getConfigValues: (group?: string) => {
    return requestBatchConfigValues(group ? { group } : {})
  },

  /**
   * 批量获取配置值（按键名）
   */
  batchGetConfigValues: (params: ConfigBatchGetParams) => {
    return requestBatchConfigValues(params)
  },

  /**
   * 更新配置值
   */
  updateConfigValues: (data: ConfigValueUpdateData) => {
    if (!data.configs?.length) {
      return Promise.resolve([])
    }

    const payload: Record<string, any> = {
      configs: data.configs.map(({ field_name, value }) => ({
        key: field_name,
        value,
      })),
    }

    if (data.environment) {
      payload.environment = data.environment
    }

    return http.post('system/configs/batch-update', payload)
  },

  /**
   * 获取配置分组列表
   */
  getConfigGroups: () => {
    return http.get<string[]>('system/configs/groups')
  },

  /**
   * 批量更新配置值（简化版）
   */
  batchUpdateConfigValues: (configs: Record<string, any>) => {
    const configArray = Object.entries(configs).map(([field_name, value]) => ({
      field_name,
      value,
    }))
    return settingsApi.updateConfigValues({ configs: configArray })
  },

  // ==================== 网站基本信息 ====================

  /**
   * 获取网站基本信息
   */
  getSiteInfo: () => {
    return http.get<SiteInfo>('settings/site-info')
  },

  /**
   * 更新网站基本信息
   */
  updateSiteInfo: (data: SiteInfoUpdateData) => {
    return http.put('settings/site-info', data)
  },

  // ==================== 系统配置 ====================

  /**
   * 获取系统配置
   */
  getSystemConfig: () => {
    return http.get<SystemConfig>('settings/system-config')
  },

  /**
   * 更新系统配置
   */
  updateSystemConfig: (data: SystemConfigUpdateData) => {
    return http.put('settings/system-config', data)
  },

  /**
   * 获取指定配置项
   */
  getConfigItem: (key: string) => {
    return http.get(`settings/config/${key}`)
  },

  /**
   * 更新指定配置项
   */
  updateConfigItem: (key: string, value: any) => {
    return http.put(`settings/config/${key}`, { value })
  },

  /**
   * 批量更新配置
   */
  batchUpdateConfig: (data: Record<string, any>) => {
    return http.post('settings/config/batch', data)
  },

  /**
   * 清除缓存
   */
  clearCache: () => {
    return http.post('settings/cache/clear')
  },

  // ==================== 站点设置 ====================

  /**
   * 获取站点设置
   */
  getSiteSettings: () => {
    return http.get<SiteSettings>('settings/site-settings')
  },

  /**
   * 更新站点设置
   */
  updateSiteSettings: (data: SiteSettingsUpdateData) => {
    return http.put('settings/site-settings', data)
  },

  // ==================== 系统字典 ====================

  /**
   * 获取字典列表
   */
  getDictionaryList: (params?: DictionaryListParams) => {
    return http.get('settings/dictionaries', { params })
  },

  /**
   * 获取字典详情
   */
  getDictionaryDetail: (id: number) => {
    return http.get(`settings/dictionaries/${id}`)
  },

  /**
   * 创建字典
   */
  createDictionary: (data: DictionaryCreateData) => {
    return http.post('settings/dictionaries', data)
  },

  /**
   * 更新字典
   */
  updateDictionary: (id: number, data: DictionaryUpdateData) => {
    return http.put(`settings/dictionaries/${id}`, data)
  },

  /**
   * 删除字典
   */
  deleteDictionary: (id: number) => {
    return http.delete(`settings/dictionaries/${id}`)
  },

  /**
   * 批量删除字典
   */
  batchDeleteDictionary: (ids: number[]) => {
    return http.post('settings/dictionaries/batch-delete', { ids })
  },

  /**
   * 根据类型获取字典列表
   */
  getDictionaryByType: (type: string) => {
    return http.get(`settings/dictionaries/type/${type}`)
  },

  // ==================== 字典类型 ====================

  /**
   * 获取字典类型列表
   */
  getDictionaryTypeList: (params?: DictionaryTypeListParams) => {
    return http.get('settings/dictionary-types', { params })
  },

  /**
   * 获取所有字典类型（用于下拉选择）
   */
  getAllDictionaryTypes: () => {
    return http.get('settings/dictionary-types/all')
  },

  /**
   * 创建字典类型
   */
  createDictionaryType: (data: DictionaryTypeCreateData) => {
    return http.post('settings/dictionary-types', data)
  },

  /**
   * 更新字典类型
   */
  updateDictionaryType: (id: number, data: DictionaryTypeUpdateData) => {
    return http.put(`settings/dictionary-types/${id}`, data)
  },

  /**
   * 删除字典类型
   */
  deleteDictionaryType: (id: number) => {
    return http.delete(`settings/dictionary-types/${id}`)
  },
}

export default settingsApi
